/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 */

/*
 * email.card — a rounded, padded SURFACE that groups child email.* blocks. It is
 * the email equivalent of the site's frosted-glass card: an elevated dark panel on
 * the (true-black) email body, with real breathing room and rounded corners.
 *
 * WHY DARK-ONLY BACKGROUNDS: every leaf email element colours its own text from
 * $.config.my.app.colors.text (#FFFFFF for this brand) with an INLINE color, which
 * beats any colour a parent could cascade. So a card can only change the SURFACE,
 * not the text — a light card would make white child text invisible. Dark elevated
 * panels keep every existing child readable with ZERO change to those elements, and
 * they are exactly the EGT look (frosted glass on black). Backgrounds are therefore
 * constrained to dark tokens (or a dark #hex); a full-bleed image is decorative and
 * always carries a solid dark fallback (email clients block images by default, and
 * no critical copy may live only in an image — put text in child blocks, not the
 * image).
 *
 * OUTLOOK: the Word engine ignores border-radius, so the card degrades to a square
 * dark panel there — still a clearly-grouped surface. The design never depends on
 * the radius. Padding is table-cell padding (Outlook-safe), never div padding.
 *
 * USAGE (in a ua/json email template):
 *   { "namespace": "email.card", "background": "surface",
 *     "inner": [ { "namespace": "email.text", "content": "…" }, … ] }
 *
 * Children are rendered via uat/src/email/renderChildren.js against the same server
 * registry MailMessage builds with. Container nesting works (a card inside a card).
 */

var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "email.card",
    title: "Email Card",
    description: "A rounded, padded dark surface that groups child email blocks — the frosted-glass card look for email (solid colour or decorative full-bleed image, always with a solid fallback).",
    supportServer: true,
    control: false,
    foruseIn: ["email"],
    fields: {
        "background": {
            title: "Background",
            type: "string",
            description: "A dark surface token (surface, panel, raise, ink) or a dark #hex. Light colours are not supported — child text is white.",
            required: false,
            default: "surface"
        },
        "image": {
            title: "Background Image",
            type: "uri",
            description: "Optional absolute URL of a decorative full-bleed background image. A solid dark colour always shows as a fallback; put no critical copy in the image.",
            required: false
        },
        "radius": {
            title: "Corner Radius",
            type: "number",
            description: "Corner radius in pixels (ignored by Outlook). Default 16.",
            required: false
        },
        "padding": {
            title: "Padding",
            type: "string",
            description: "CSS padding for the card interior (1–4 space-separated lengths). Default '28px'.",
            required: false
        },
        "border": {
            title: "Border",
            type: "string",
            description: "'none', or a #hex for a 1px border. Default is a subtle hairline that lifts the card off black.",
            required: false
        },
        "align": {
            title: "Align",
            type: "string",
            description: "Horizontal alignment of the card interior: left | center | right. Default left.",
            required: false,
            default: "left"
        },
        "inner": {
            title: "Blocks",
            type: "inner",
            description: "The child email.* blocks this card contains.",
            required: false
        }
    }
});

var $ = require("../../../uam/scafolding.js");
var urls = require("../email/urls.js");

// SURFACE TOKENS ARE CONFIG-DRIVEN (tenant-neutral contract). The named tokens a
// template picks (surface/panel/raise/ink) resolve to hexes from
// my.app.surfaces.*; EGT's def.config.json sets the dark EGT palette below, so its
// output is unchanged. A tenant that configures no surfaces gets a NEUTRAL palette
// (never EGT's black) — see NEUTRAL_SURFACES.
//
// SEAM / FOUNDER OPEN QUESTION (light surfaceMode, DEFERRED in M4): email.card is
// "dark-only by construction" — every leaf child inks its own text WHITE from
// my.app.colors.text, which beats any surface a parent sets. So a light neutral
// surface + white child text would be invisible. A full LIGHT surfaceMode (that
// also flips child text colour) is a design decision the founder still owns; this
// code only guarantees the FALLBACK is neutral, not a finished light theme. A
// non-dark tenant should set my.app.surfaces (and eventually a surfaceMode) itself.
var NEUTRAL_SURFACES = {
    surface: "#F4F4F5",
    panel: "#FFFFFF",
    raise: "#FAFAFA",
    ink: "#E9E9EB",
    border: "#E5E5E5"
};

// Resolve the token palette from config, neutral fallback per token.
function surfacesFromConfig() {
    var s = ($.config && $.config.my && $.config.my.app && $.config.my.app.surfaces) || {};
    return {
        surface: s.surface || NEUTRAL_SURFACES.surface,
        panel: s.panel || NEUTRAL_SURFACES.panel,
        raise: s.raise || NEUTRAL_SURFACES.raise,
        ink: s.ink || NEUTRAL_SURFACES.ink,
        border: s.border || NEUTRAL_SURFACES.border
    };
}

// Escape a value for safe insertion into a double-quoted HTML attribute.
function escAttr(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// Make a URL safe to sit inside a CSS url('…') string that itself sits inside a
// double-quoted HTML style attribute. escAttr covers the attribute layer; this
// ALSO percent-encodes the characters that could break out of the CSS string /
// url() context — single quote, parentheses, backslash and any whitespace — so a
// crafted image URL can never inject a CSS declaration. A well-formed URL never
// contains these raw, so encoding them is loss-free in practice.
function cssUrl(s) {
    var enc = String(s == null ? "" : s).replace(/['()\\\s]/g, function (c) {
        return "%" + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0");
    });
    return escAttr(enc);
}

// Resolve a possibly-relative image path to an ABSOLUTE URL against the PUBLIC
// email origin (never config.my.app.url — that is the ENV host and would ship a
// dev .test URL into a recipient's inbox). Mirrors email.image.js.
function toAbsolute(src) {
    var s = (typeof src === "string") ? src.trim() : "";
    if (!s) { return ""; }
    if (/^(https?:)?\/\//i.test(s) || /^data:/i.test(s)) { return s; }
    var joined = urls.emailUrl($.config || {}, s);
    return joined || s;
}

var HEX = /^#[0-9A-Fa-f]{3,8}$/;

// Resolve the card surface colour: a named token (from config), a #hex, else the
// configured/neutral default. `palette` is surfacesFromConfig() for this render.
function resolveBackground(value, palette) {
    if (typeof value === "string") {
        var v = value.trim();
        if (palette[v.toLowerCase()]) { return palette[v.toLowerCase()]; }
        if (HEX.test(v)) { return v; }
    }
    return palette.surface;
}

// Resolve the border declaration. Default: a hairline that lifts the card off the
// body (config-driven colour; neutral fallback). "none" removes it. A #hex sets a
// 1px solid border of that colour.
function resolveBorder(value, palette) {
    if (typeof value === "string") {
        var v = value.trim().toLowerCase();
        if (v === "none" || v === "0" || v === "false") { return ""; }
        if (HEX.test(value.trim())) { return "border:1px solid " + value.trim() + ";"; }
    }
    return "border:1px solid " + palette.border + ";";
}

// Sanitize a CSS padding string to 1–4 space-separated lengths (px/rem/em/%). A
// malformed value falls back to the default so a template can never inject CSS.
function resolvePadding(value, fallback) {
    if (typeof value === "string") {
        var v = value.trim();
        if (/^(\d+(\.\d+)?(px|rem|em|%)\s*){1,4}$/.test(v + " ")) { return v; }
    }
    return fallback;
}

function resolveAlign(value) {
    var v = (typeof value === "string") ? value.trim().toLowerCase() : "";
    return (v === "center" || v === "right") ? v : "left";
}

function resolveRadius(value) {
    var n = parseInt(value, 10);
    if (!(n >= 0) || n > 40) { return 16; }
    return n;
}

exports.renderEmail = async function (options) {
    options = options || {};

    // Lazy requires: at registry-build time this element is require()d WHILE the
    // registry module is mid-load, so requiring the registry at module scope would
    // be a circular dependency. Deferring to call time breaks the cycle.
    var registry = require("../registry.app.server.js");
    var renderChildren = require("../email/renderChildren.js").renderChildren;

    var kids = options.inner || options.i || options.content || [];
    var rendered = await renderChildren(registry, kids);

    var palette = surfacesFromConfig();
    // Body font: config-driven, neutral Arial fallback (NEVER a serif default).
    var bodyFont = ($.config && $.config.my && $.config.my.app && $.config.my.app.fonts && $.config.my.app.fonts.body) || "Arial,Helvetica,sans-serif";

    var bg = resolveBackground(options.background, palette);
    var radius = resolveRadius(options.radius);
    var padding = resolvePadding(options.padding, "28px");
    var border = resolveBorder(options.border, palette);
    var align = resolveAlign(options.align);

    var bgStyle = "background-color:" + bg + ";";
    var img = options.image ? toAbsolute(options.image) : "";
    if (img) {
        // Decorative full-bleed image with the solid colour as the ALWAYS-present
        // fallback (blocked images / Outlook show the colour).
        bgStyle += "background-image:url('" + cssUrl(img) + "');background-position:center center;background-size:cover;background-repeat:no-repeat;";
    }

    var html = `<tr>
  <td style="padding:8px 0;">
    <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="width:100%;border-radius:${radius}px;${border}${bgStyle}">
      <tr>
        <td align="${align}" style="padding:${padding};font-family:${bodyFont};">
          <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="width:100%;">
            ${rendered.html}
          </table>
        </td>
      </tr>
    </table>
  </td>
</tr>`;

    return { html: html, text: rendered.text };
};

// The editor/preview render path (browser-safe): show the compiled card HTML inside
// the server-not-supported wrapper, exactly like the other email.* elements.
exports.render = async function (options) {
    var { html } = await exports.renderEmail(options);
    return [{
        n: "ua.element.serverNotSupported",
        inner: {
            n: "json",
            type: "ua/interface",
            by: "email.card",
            i: JSON.stringify({
                n: "html",
                html: `<div style="max-width:480px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;">${html}</div>`
            })
        }
    }];
};
